import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import api from '../../api';
import BuysRegisterTab from '../../components/BudgetTabs/BuysRegisterTab';
import BudgetReportsTab from '../../components/BudgetTabs/BudgetReportsTab';
import Receipts from '../../components/table/Receipts';
import NioktrOwnFundsTab from '../../components/BudgetTabs/NioktrOwnFundsTab';
import BackBtn from '../../components/UI/BackBtn';
import { Tab, Tabs } from '../../components/Tabs';
import useNavProject from '../../hooks/useNavProject';
import { isAdmin, isPortfolioAdmin, isProjectAdmin, isStratAdmin, isPolicyAdmin, isModerator, isStratCurator, isAdminReadRestricted } from '../../services/rolesSvc';
import useUserContext from '../../hooks/useUserContext';

const tabs = [
  {
    label: 'buysRegister',
    tabName: 'Реестр закупок',
    content: BuysRegisterTab,
  },
  {
    label: 'budgetReports',
    tabName: 'Отчеты по бюджету',
    content: BudgetReportsTab,
    show: (user, stratId, portfId, projId) => {
      //отчеты оставить, если есть СП или уровень проекта, у политик не показывать
      const isPoliciesRoute = window.location.pathname.startsWith('/policies');
      if (isPoliciesRoute) return false;
      
      return (stratId || projId) && (isAdmin(user) || isStratAdmin(user, stratId) || isPortfolioAdmin(user, portfId) || isProjectAdmin(user, projId));
    },
  },
  {
    label: 'nioktrReceipts',
    tabName: 'Поступления от НИОКТР',
    leftValues: [140, 357, 533],
    totalIndex: 3,
    maxBodyHeight: 'calc(100vh - 540px)',
    content: Receipts,
    show: (user, stratId, portfId, projId, policyId, strategy) => strategy?.hasContracts && (isAdminReadRestricted(user) || isPolicyAdmin(user, policyId) || isModerator(user) || isStratAdmin(user, 4) || isStratCurator(user, 4)),
  },
  {
    label: 'nioktrOwnFunds',
    tabName: 'Собственные средства, направленные на НИОКТР',
    leftValues: [140, 357, 533],
    totalIndex: 3,
    maxBodyHeight: 'calc(100vh - 540px)',
    content: NioktrOwnFundsTab,
    // content: Receipts,
    show: (user, stratId, portfId, projId, policyId, strategy) => strategy?.hasContracts && (isAdminReadRestricted(user) || isPolicyAdmin(user, policyId) || isModerator(user) || isStratAdmin(user, 4) || isStratCurator(user, 4)),
  },
];

const skipTabCondition = (isPortfolioPage, tabLabel) => isPortfolioPage && tabLabel === 'budgetReports';

export default function Budget() {
  const { stratId, portfolioId, projId } = useParams();
  const [proj, setProj] = useState({});
  const [navCtx, setNavCtx] = useState(null);
  const navProject = useNavProject();
  const userCtx = useUserContext();
  const history = useHistory();

  useEffect(async () => {
    if (portfolioId && !projId) {
      const respPortfolio = await api.get(`/structure/portfolios/${portfolioId}?latest=true`);
      if (!respPortfolio || respPortfolio.error) return;

      setNavCtx({ strategy: respPortfolio.strategy, portfolio: respPortfolio });
      navProject.setStratTitle(respPortfolio.strategy.name);
      navProject.setPortfolioTitle(respPortfolio.name);
    } else if (projId) {
      const respProject = await api.get(`/structure/projs/${projId}?latest=true`);
      if (!respProject || respProject.error) return;
      setProj(respProject);
      setNavCtx({
        project: respProject,
        strategy: respProject.strategy,
        policy: respProject.policy,
        portfolio: respProject.portfolio
      });

      if (portfolioId || respProject.portfolio) {
        navProject.setPortfProjTitle(respProject.name);
        navProject.setPortfolioTitle(respProject.portfolio.name); // нужен ли originId портфеля?
      } else {
        navProject.setProjTitle(respProject.name);
      }
      if (!stratId && (respProject.strategy.type === 'policy')) {
        navProject.setPolicyTitle(respProject.policy.name);
      } else {
        navProject.setStratTitle(respProject.strategy.name);
      }
    } else {
      const respStrategy = await api.get(`/mng/strategies/${stratId}`);
      if (!respStrategy || respStrategy.error) return;

      setNavCtx({ strategy: respStrategy });
      navProject.setStratTitle(respStrategy.name);
    }
  }, [])

  useEffect(() => {
    if(navCtx && !navCtx.project && !navCtx.portfolio && navCtx.strategy && 
       navCtx.strategy.type === 'policy' && 
       !navCtx.strategy.hasProjects && 
       !navCtx.strategy.hasContracts && 
       !history.location.pathname.includes('/archive/')){
      history.replace(`/policies/${navCtx.strategy.id}`)
    }
  }, [navCtx])

  const pageTitle = useMemo(() => navCtx?.strategy
    ? (navCtx.project || navCtx.portfolio || navCtx.strategy).name
    : navCtx?.policy?.name, [navCtx]);

  if (navCtx && (navCtx.project || navCtx.portfolio || navCtx.strategy)) {
    return (
      <section className="page strat-proj-report-page">
        <div style={{ padding: '2.5rem' }}>
          <div style={{ marginBottom: 16 }}>
            <BackBtn />
          </div>
          <h4 style={styledSubHeading}>{pageTitle}</h4>
          <h3 style={styledHeading}>Управление бюджетом</h3>
          <Tabs>
            {tabs.filter((t) => !skipTabCondition(portfolioId && !projId, t.label)
                        && !t.show || t.show(userCtx, navCtx.strategy?.id, navCtx.portfolio?.id, navCtx.project?.originId, navCtx.policy?.id, navCtx?.strategy))
              .map((tab, i) => (
                <Tab key={i} label={tab.label} tabName={tab.tabName}>
                  <tab.content
                    projectId={navCtx.project?.id}
                    projectStatus={navCtx.project?.status}
                    projectNum={navCtx.project?.fullNumber}
                    originId={navCtx.project?.originId}
                    stratId={navCtx.strategy?.id}
                    strategyNum={navCtx.strategy?.num}
                    strategyName={navCtx.strategy?.name}
                    strategyType={navCtx.strategy?.type}
                    policyId={navCtx.strategy?.id}
                    policyName={navCtx.policy?.name}
                    portfolioId={navCtx.portfolio?.id}
                    tableLeftValues={tab.leftValues}
                    type={tab.label}
                    totalIndex={tab.totalIndex}
                    maxBodyHeight={tab.maxBodyHeight}
                    proj={proj}
                    policyMode={true}
                  />
                </Tab>
              ))}
          </Tabs>
        </div>
      </section>
    );
  }

  return <div style={{ color: 'red', background: 'white' }}>Загрузка...</div>;
}

const styledHeading = { marginBottom: 24, textAlign: 'left' };
const styledSubHeading = { marginBottom: 24, marginTop: 36, lineHeight: '23px' };
